/**
 * CodingPane -- Monaco-based live code editor for coding interviews.
 *
 * Every edit fires `onChange(content, language)`. The parent owns
 * debouncing via `useArtifactSync` -- we just report what's in the
 * editor and let the hook decide when to push it over the WS.
 *
 * Discrete events ("Run" / "Submit") call `onEvent('submit', details)`.
 * For Phase 2 there's no real code execution, so "Submit" is purely a
 * signal to the AI that the candidate considers their solution ready.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Editor from '@monaco-editor/react';
import { Play, Send, ChevronDown, Eye } from 'lucide-react';

const SUPPORTED_LANGUAGES: { id: string; label: string }[] = [
  { id: 'python',      label: 'Python' },
  { id: 'javascript',  label: 'JavaScript' },
  { id: 'typescript',  label: 'TypeScript' },
  { id: 'java',        label: 'Java' },
  { id: 'go',          label: 'Go' },
  { id: 'rust',        label: 'Rust' },
  { id: 'cpp',         label: 'C++' },
  { id: 'csharp',      label: 'C#' },
  { id: 'sql',         label: 'SQL' },
  { id: 'plaintext',   label: 'Plain text' },
];

// Deliberately neutral starters: just a single comment, no fictional
// `solve(nums, target)` signature. The exercise the AI gives can be a
// LeetCode problem, an object-oriented design, a SQL query or a quick
// script -- pre-baking a twoSum-style signature was misleading the
// candidate into the wrong shape.
const STARTER_CODE: Record<string, string> = {
  python:     "# Type your solution here\n",
  javascript: "// Type your solution here\n",
  typescript: "// Type your solution here\n",
  java:       "// Type your solution here\n",
  go:         "// Type your solution here\n",
  rust:       "// Type your solution here\n",
  cpp:        "// Type your solution here\n",
  csharp:     "// Type your solution here\n",
  sql:        "-- Type your query here\n",
  plaintext:  "",
};

interface CodingPaneProps {
  lang: 'de' | 'en';
  onChange: (payload: { content: string; language: string; char_count: number }) => void;
  onEvent: (action: 'run' | 'submit' | 'clear', details?: Record<string, unknown>) => void;
  disabled?: boolean;
  /** Epoch ms of the last snapshot pushed to the server (drives the
      "AI sees your code" indicator). */
  lastPushedAt?: number | null;
  pendingEdit?: boolean;
}

const CodingPane: React.FC<CodingPaneProps> = ({
  lang,
  onChange,
  onEvent,
  disabled,
  lastPushedAt,
  pendingEdit,
}) => {
  const [language, setLanguage] = useState<string>('python');
  const [content, setContent] = useState<string>(STARTER_CODE.python);
  // Make the badge briefly pulse green right after each push.
  const [justPushed, setJustPushed] = useState(false);
  useEffect(() => {
    if (!lastPushedAt) return;
    setJustPushed(true);
    const t = setTimeout(() => setJustPushed(false), 1500);
    return () => clearTimeout(t);
  }, [lastPushedAt]);

  const handleEditorChange = useCallback(
    (value: string | undefined) => {
      const v = value ?? '';
      setContent(v);
      onChange({ content: v, language, char_count: v.length });
    },
    [language, onChange],
  );

  const handleLanguageChange = useCallback(
    (newLang: string) => {
      setLanguage(newLang);
      // If the editor is still on the starter for the previous lang, swap
      // to the new lang's starter so the candidate isn't left with stale
      // code in the wrong language.
      const prevStarter = Object.values(STARTER_CODE).find((s) => s === content);
      if (prevStarter !== undefined) {
        const newContent = STARTER_CODE[newLang] || '';
        setContent(newContent);
        onChange({ content: newContent, language: newLang, char_count: newContent.length });
      } else {
        onChange({ content, language: newLang, char_count: content.length });
      }
    },
    [content, onChange],
  );

  const t = useMemo(
    () => ({
      languageLabel: lang === 'de' ? 'Sprache' : 'Language',
      submit:        lang === 'de' ? 'Lösung absenden' : 'Submit solution',
      submitHint:    lang === 'de'
        ? 'Sagt der KI: „Ich bin fertig". Sie gibt dir dann Feedback.'
        : 'Tells the AI "I\'m done". They will respond with feedback.',
      runHint:       lang === 'de'
        ? '(Code-Execution kommt später)'
        : '(Code execution coming later)',
      aiSeesPushed:  lang === 'de' ? 'AI sieht deinen Code' : 'AI sees your code',
      aiSeesPending: lang === 'de' ? 'Wird an die AI gesendet ...' : 'Syncing to AI ...',
      aiSeesIdle:    lang === 'de' ? 'Tippe - die AI schaut zu' : 'Type - the AI is watching',
    }),
    [lang],
  );

  const awarenessLabel = pendingEdit
    ? t.aiSeesPending
    : lastPushedAt
      ? t.aiSeesPushed
      : t.aiSeesIdle;

  return (
    <div className="flex flex-col h-full bg-[#0d1117] rounded-2xl border border-white/10 overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wider text-white/40 font-medium">
            {t.languageLabel}
          </label>
          <div className="relative">
            <select
              value={language}
              onChange={(e) => handleLanguageChange(e.target.value)}
              disabled={disabled}
              className="appearance-none rounded-md border border-white/10 bg-white/[0.04] pl-2 pr-6 py-1 text-xs text-white/85 hover:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-pink-400/30 disabled:opacity-50 cursor-pointer"
            >
              {SUPPORTED_LANGUAGES.map((l) => (
                <option key={l.id} value={l.id} className="bg-[#0d1117]">
                  {l.label}
                </option>
              ))}
            </select>
            <ChevronDown className="w-3 h-3 text-white/40 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          {/* Awareness indicator - tells the candidate the AI is reading
              their editor live. Flashes green right after each WS push. */}
          <span
            className={[
              'ml-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium border transition-colors',
              justPushed
                ? 'bg-emerald-500/15 border-emerald-400/40 text-emerald-200'
                : pendingEdit
                  ? 'bg-amber-500/10 border-amber-400/30 text-amber-200/90'
                  : 'bg-white/[0.04] border-white/10 text-white/55',
            ].join(' ')}
            title={awarenessLabel}
          >
            <span className="relative inline-flex h-1.5 w-1.5">
              <span
                className={[
                  'absolute inline-flex h-full w-full rounded-full opacity-75',
                  justPushed
                    ? 'bg-emerald-400 animate-ping'
                    : pendingEdit
                      ? 'bg-amber-400 animate-pulse'
                      : 'bg-white/30',
                ].join(' ')}
              />
              <span
                className={[
                  'relative inline-flex h-1.5 w-1.5 rounded-full',
                  justPushed ? 'bg-emerald-400' : pendingEdit ? 'bg-amber-400' : 'bg-white/40',
                ].join(' ')}
              />
            </span>
            <Eye className="w-3 h-3" />
            {awarenessLabel}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            disabled
            title={t.runHint}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-white/40 bg-white/[0.04] border border-white/10 cursor-not-allowed"
          >
            <Play className="w-3 h-3" />
            Run
          </button>
          <button
            onClick={() => onEvent('submit', { language, char_count: content.length })}
            disabled={disabled}
            title={t.submitHint}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold text-white bg-gradient-to-r from-pink-500 to-orange-400 hover:from-pink-600 hover:to-orange-500 disabled:opacity-40"
          >
            <Send className="w-3 h-3" />
            {t.submit}
          </button>
        </div>
      </div>

      {/* Monaco */}
      <div className="flex-1 min-h-0">
        <Editor
          value={content}
          language={language}
          theme="vs-dark"
          onChange={handleEditorChange}
          options={{
            fontSize: 13,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            tabSize: 2,
            automaticLayout: true,
            renderLineHighlight: 'gutter',
            lineNumbers: 'on',
            padding: { top: 12, bottom: 12 },
          }}
        />
      </div>
    </div>
  );
};

export default CodingPane;
